import {
  authService,
  MeAccessFeature,
  UserAccess,
  UserProfile,
} from "@/services/authService";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useSelectedHotelId } from "@/utils/hotelContext";
import { tenantSubscriptionService } from "@/services/tenant-subscription-service";
import { HotelSubscriptionFeature } from "@/types/hotel-subscription";
import { isHotelScopedSubscriptionRole } from "@/utils/accessControl";
import {
  TenantMergedFeature,
  TenantMergedFeaturesResponse,
  TenantSubscription,
} from "@/types/tenant-subscription";
import { Role } from "@/types/user";
import { useIsAuthenticated } from "@/hooks/useIsAuthenticated";

interface UseAuthUserReturn {
  isAuthenticated: boolean;
  userProfile: UserProfile | null;
  isLoading: boolean;
  error: Error | null;
}

function isHotelSubscriptionActive(status?: string | null): boolean {
  return status === "ACTIVE" || status === "TRIALING";
}

function parseBooleanFeatureValue(feature: HotelSubscriptionFeature): boolean {
  if (!feature.enabled) {
    return false;
  }

  if (typeof feature.value === "boolean") {
    return feature.value;
  }

  if (typeof feature.value === "string") {
    if (feature.value === "true") {
      return true;
    }

    if (feature.value === "false") {
      return false;
    }
  }

  return true;
}

function parseNumericFeatureValue(
  feature: HotelSubscriptionFeature,
): number | "unlimited" | null {
  if (!feature.enabled) {
    return null;
  }

  if (feature.value === "unlimited") {
    return "unlimited";
  }

  if (typeof feature.value === "number" && Number.isFinite(feature.value)) {
    return feature.value;
  }

  if (typeof feature.value === "string") {
    const parsed = Number(feature.value);

    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
}

function mapHotelSubscriptionFeatures(
  features: HotelSubscriptionFeature[],
): MeAccessFeature[] {
  return features.reduce<MeAccessFeature[]>((accumulator, feature) => {
    if (feature.type === "BOOLEAN") {
      accumulator.push({
        key: feature.key,
        type: "BOOLEAN",
        value: parseBooleanFeatureValue(feature),
      });
      return accumulator;
    }

    if (feature.type === "NUMBER") {
      const value = parseNumericFeatureValue(feature);

      if (value === null) {
        return accumulator;
      }

      accumulator.push({
        key: feature.key,
        type: "NUMBER",
        value,
      });
      return accumulator;
    }

    accumulator.push({
      key: feature.key,
      type: "BOOLEAN",
      value: feature.enabled,
    });
    return accumulator;
  }, []);
}

function mapTenantMergedFeatures(
  features: TenantMergedFeature[],
): MeAccessFeature[] {
  return features.reduce<MeAccessFeature[]>((accumulator, feature) => {
    if (feature.type === "NUMBER") {
      accumulator.push({
        key: feature.key,
        type: "NUMBER",
        value: feature.value,
      });
      return accumulator;
    }

    accumulator.push({
      key: feature.key,
      type: "BOOLEAN",
      value: feature.value === true,
    });
    return accumulator;
  }, []);
}

function buildSelectedHotelAccess(
  userProfile: UserProfile,
  selectedHotelSubscription:
    | Awaited<ReturnType<typeof tenantSubscriptionService.getHotelSubscription>>
    | undefined,
  isLoading: boolean,
): UserAccess | null {
  if (isLoading && selectedHotelSubscription === undefined) {
    // `/auth/me` is not keyed by the selected hotel, so its access belongs to
    // the previously resolved hotel while a switch is in progress. Returning
    // it here briefly enables paid features (including Stream Chat) for the
    // next hotel and causes those consumers to mount before its subscription
    // has been resolved.
    return null;
  }

  return {
    hasActiveSubscription: isHotelSubscriptionActive(
      selectedHotelSubscription?.status ?? null,
    ),
    // The hotel subscription endpoint returns the effective feature set for
    // the selected hotel. Preserve the plan-level core bundle flag separately.
    enableCoreFeatures:
      selectedHotelSubscription?.plan?.enableCoreFeatures ?? false,
    currentPlan: selectedHotelSubscription?.plan ?? null,
    tenantSubscriptionId:
      selectedHotelSubscription?.tenantSubscriptionId ?? null,
    paymentUrl: userProfile.access?.paymentUrl ?? null,
    features: mapHotelSubscriptionFeatures(
      selectedHotelSubscription?.features ?? [],
    ),
  };
}

function buildTenantMergedAccess(
  userProfile: UserProfile,
  tenantMergedFeatures: TenantMergedFeaturesResponse | undefined,
  tenantSubscriptions: TenantSubscription[] | undefined,
  isTenantMergedFeaturesLoading: boolean,
  isTenantSubscriptionsLoading: boolean,
): UserAccess | null {
  if (
    (isTenantMergedFeaturesLoading && tenantMergedFeatures === undefined) ||
    (isTenantSubscriptionsLoading && tenantSubscriptions === undefined)
  ) {
    return userProfile.access ?? null;
  }

  return {
    hasActiveSubscription: (tenantSubscriptions?.length ?? 0) > 0,
    // `/subscriptions/my` returns active subscriptions with their effective
    // plan snapshots, so core access must be aggregated from those plans.
    enableCoreFeatures:
      tenantSubscriptions?.some(
        (subscription) => subscription.plan.enableCoreFeatures === true,
      ) ?? false,
    currentPlan: null,
    tenantSubscriptionId: userProfile.access?.tenantSubscriptionId ?? null,
    paymentUrl: userProfile.access?.paymentUrl ?? null,
    features: mapTenantMergedFeatures(tenantMergedFeatures?.features ?? []),
  };
}

export const useAuthenticatedUser = (
  disableToast: boolean = false,
  retry: number | boolean = 2,
): UseAuthUserReturn => {
  // Only fetch the profile once an access token exists. Without this guard the
  // query fires on app boot (e.g. via the globally-mounted NotificationsToast
  // provider) while the OAuth callback is still exchanging the code, sending a
  // tokenless GET /auth/me that 401s and triggers a logout + redirect to login.
  const hasAccessToken = useIsAuthenticated();
  const {
    data: userProfile,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: () =>
      authService.getMe({
        disableToast,
      }),
    enabled: hasAccessToken,
    retry,
  });

  const selectedHotelIdFromStorage = useSelectedHotelId();
  const selectedHotelId =
    selectedHotelIdFromStorage ?? userProfile?.profile?.hotelId ?? null;
  const canReadSelectedHotelSubscription = isHotelScopedSubscriptionRole(
    userProfile ?? null,
  );
  const shouldUseSelectedHotelSubscription = Boolean(
    userProfile && canReadSelectedHotelSubscription && selectedHotelId,
  );
  const canReadTenantMergedFeatures = userProfile?.role === Role.TENANT_ADMIN;

  const {
    data: selectedHotelSubscription,
    isLoading: isSelectedHotelSubscriptionLoading,
  } = useQuery({
    queryKey: ["selectedHotelSubscription", selectedHotelId],
    queryFn: () =>
      tenantSubscriptionService.getHotelSubscription(selectedHotelId ?? ""),
    enabled: Boolean(
      userProfile && canReadSelectedHotelSubscription && selectedHotelId,
    ),
    // Deliberately no `placeholderData` here: this query is keyed by
    // `selectedHotelId`, so keeping the previous hotel's data as a
    // placeholder while switching would let `buildSelectedHotelAccess`
    // briefly compute access from the WRONG hotel's subscription — causing
    // the dashboard's widget set and SubscriptionLockOverlay (which both
    // derive from `access.hasActiveSubscription`) to flicker between two
    // inconsistent states before the real data for the new hotel arrives.
    staleTime: 60 * 1000,
  });

  const {
    data: tenantMergedFeatures,
    isLoading: isTenantMergedFeaturesLoading,
  } = useQuery({
    queryKey: ["tenantMergedFeatures"],
    queryFn: () => tenantSubscriptionService.getMyFeatures(),
    enabled: Boolean(userProfile && canReadTenantMergedFeatures),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  const { data: tenantSubscriptions, isLoading: isTenantSubscriptionsLoading } =
    useQuery({
      queryKey: ["tenantSubscriptions", "currentTenant"],
      queryFn: () => tenantSubscriptionService.getMySubscriptions(),
      enabled: Boolean(userProfile && canReadTenantMergedFeatures),
      placeholderData: keepPreviousData,
      staleTime: 60 * 1000,
    });

  const shouldUseTenantMergedSubscription = Boolean(
    userProfile && canReadTenantMergedFeatures,
  );

  let effectiveUserProfile = userProfile;

  if (userProfile && shouldUseSelectedHotelSubscription) {
    effectiveUserProfile = {
      ...userProfile,
      access: buildSelectedHotelAccess(
        userProfile,
        selectedHotelSubscription,
        isSelectedHotelSubscriptionLoading,
      ),
    };
  } else if (userProfile && shouldUseTenantMergedSubscription) {
    effectiveUserProfile = {
      ...userProfile,
      access: buildTenantMergedAccess(
        userProfile,
        tenantMergedFeatures,
        tenantSubscriptions,
        isTenantMergedFeaturesLoading,
        isTenantSubscriptionsLoading,
      ),
    };
  }

  return {
    isAuthenticated:
      !isLoading &&
      !isSelectedHotelSubscriptionLoading &&
      !isTenantMergedFeaturesLoading &&
      !isTenantSubscriptionsLoading &&
      !!effectiveUserProfile,
    userProfile: effectiveUserProfile || null,
    isLoading:
      isLoading ||
      isSelectedHotelSubscriptionLoading ||
      isTenantMergedFeaturesLoading ||
      isTenantSubscriptionsLoading,
    error: error instanceof Error ? error : null,
  };
};
